import {
  ActivitiesApi,
  ScormApi,
  TrainingsLmsApi,
  type ActivitiesResponse,
  type ActivitiesUpdateQuizDto,
  type ActivityListItem,
  ActivitiesResponseTypeEnum,
} from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import type { ErrorObject } from "@vuelidate/core";
import { useCreateBuilderTrainingStore } from "../trainings/create-training-builder.store";
import { useActivityEditorStore } from "./activity-editor.store";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import type { IActivitiesStore, IActivityOwner } from "~/utils/interfaces/activities-interfaces";
import { TypeBuilderPanelEnum } from "~/utils/enums/training.enums";
import { ActivityItemTypeEnum, type QuizTypeEnum } from "~/utils/enums/activities.enums";

// The activities API scopes every write by its owner: here, a training of the builder.
const trainingOwner = (trainingId: string): IActivityOwner => ({
  itemType: ActivityItemTypeEnum.TRAINING,
  itemId: trainingId,
});
const builderTrainingOwner = (): IActivityOwner =>
  trainingOwner(useCreateBuilderTrainingStore().training.content.trainingId ?? "");

// orders attaches a quiz to a quiz activity only: a quiz type picked right after « Quiz » waits for that type to land.
let typeUpdate: Promise<unknown> = Promise.resolve();

export const useActivitiesStore = defineStore("activitiesStore", {
  state: (): IActivitiesStore => {
    return {
      activities: [],
      currentActivity: null,
      isPreview: false,
      errors: [],
      quizzesErrors: [],
      isLoaded: false,
    };
  },
  getters: {
    getActivityById: (state) => (id: string) => {
      return state.activities?.find((activity: ActivityListItem) => {
        if (activity.activityId && activity.activityId === id) {
          return activity;
        }
        return null;
      });
    },
  },
  actions: {
    async createActivityQuiz(activityId: string, type: QuizTypeEnum, trainingId: string) {
      const feedback = useFeedback();

      try {
        await typeUpdate;
        const quiz = await useActivityEditorStore().createQuiz(trainingOwner(trainingId), activityId, type);

        if (quiz) {
          await this.getActivitiesList(trainingId);
          await this.getActivityDetails(activityId);
        }
        return null;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    async createActivity(trainingId: string) {
      this.isLoaded = false;
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);

      try {
        const activity = await useActivityEditorStore().create(trainingOwner(trainingId));

        if (activity) {
          const activitiesList = await apiInstance.getActivities({ trainingId });

          this.activities = [...activitiesList];
          this.currentActivity = activity;
          return activity.activityId;
        }
        return null;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    async getActivityDetails(activityId: string) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);

      try {
        const activity = await apiInstance.getActivity({
          activityId,
        });

        this.currentActivity = activity;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    async getActivitiesList(trainingId: string) {
      if (trainingId) {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);
        const activities = await apiInstance.getActivities({ trainingId });
        this.activities = activities;
      }
    },

    async updateActivityQuiz(edited: ActivitiesResponse, quizId: string, elements: string[]) {
      const feedback = useFeedback();

      try {
        const currentQuiz = edited.quiz?.quizId === quizId ? edited.quiz : null;
        if (currentQuiz) {
          const updateQuizDto = {} as any;
          for (const element of elements) {
            updateQuizDto[element as keyof ActivitiesUpdateQuizDto] = currentQuiz[element as keyof typeof currentQuiz];
          }
          updateQuizDto.subType = currentQuiz.subType;
          const quiz = await useActivityEditorStore().updateQuiz(
            builderTrainingOwner(),
            edited.activityId,
            quizId,
            updateQuizDto,
          );

          const quizItem = this.currentActivity?.quiz;
          if (quizItem?.quizId === quiz.quizId && !quizItem.answers?.length) {
            quizItem.answers = quiz.answers;
          }
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    // Saves these fields of the activité they were edited on, which may no longer be the open one. Resolves with the
    // activité as orders stored it, or null when nothing was stored.
    async updateActivity(edited: ActivitiesResponse, element: string[], trainingId: string) {
      const feedback = useFeedback();
      let stored: ActivitiesResponse | null = null;
      try {
        // Create payload for the update
        const payload: any = {};
        for (const el of element) {
          if (edited.activityId && Object.prototype.hasOwnProperty.call(edited, el)) {
            // add property to the payload object
            payload[el] = edited[el as keyof typeof edited];
          }
        }

        // Check if the payload have at least 1 key
        if (Object.keys(payload).length !== 0 && edited.activityId) {
          const request = useActivityEditorStore().update(trainingOwner(trainingId), edited.activityId, payload);
          // Set before any await: the quiz types show as soon as « Quiz » is picked.
          if (payload.type) typeUpdate = request.catch(() => undefined);
          const activity = await request;
          stored = activity;
          if (activity.type === ActivitiesResponseTypeEnum.Quiz && payload.type) {
            await this.getActivityDetails(activity.activityId);
          }
          if (activity) {
            await this.getActivitiesList(trainingId);
          }
          this.isLoaded = true;
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
      return stored;
    },
    async duplicateActivityQuiz(quizId: string, activityId: string) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);

      try {
        const activityQuiz = await apiInstance.duplicateActivityQuiz({
          quizId,
          activityId,
        });

        if (activityQuiz) {
          await this.getActivityDetails(activityId);
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    async duplicateActivity(activityId: string, trainingId: string) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);

      try {
        const activity = await useActivityEditorStore().duplicate(trainingOwner(trainingId), activityId);

        if (activity) {
          const activitiesList = await apiInstance.getActivities({ trainingId });
          this.currentActivity = activity;
          this.activities = [...activitiesList];
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    async removeActivity(activityId: string, trainingId: string) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ActivitiesApi);
      const createBuilderTrainingStore = useCreateBuilderTrainingStore();
      try {
        await useActivityEditorStore().remove(trainingOwner(trainingId), activityId);

        const activitiesList = await apiInstance.getActivities({ trainingId });
        this.activities = [...activitiesList];

        if (this.activities.length > 0) {
          createBuilderTrainingStore.currentTab = TypeBuilderPanelEnum.ACTIVITY;
          this.getActivityDetails(this.activities[this.activities.length - 1].activityId);
        } else {
          createBuilderTrainingStore.currentTab = TypeBuilderPanelEnum.TRAINING;
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    async updateActivityOrder(trainingId: string) {
      const feedback = useFeedback();
      const apiInstanceTraining = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);

      try {
        await apiInstanceTraining.updateActivitiesOrder({
          trainingId,
          updateLMSTrainingActivitiesOrdersDto: {
            activitiesOrder: this.activities.map((a) => a.activityId),
          },
        });
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
    pushValidationErrors(errors: ErrorObject[], activityId: string) {
      errors.forEach((err: ErrorObject) => {
        this.errors.push({ error: { [err.$propertyPath]: err.$message as string }, activityId });
      });
    },
    pushValidationQuizzesErrors(errors: ErrorObject[], activityId: string, quizId?: string) {
      errors.forEach((err: ErrorObject) => {
        this.quizzesErrors.push({
          error: { [err.$propertyPath]: err.$message as string },
          activityId,
          ...(quizId && { quizId }),
        });
      });
    },
    async uploadActivityDocument(file: File, activityId: string) {
      const feedback = useFeedback();
      try {
        this.currentActivity = await useActivityEditorStore().uploadDocument(builderTrainingOwner(), activityId, file);
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    // A video uploaded to Vimeo that its activité no longer holds.
    async deleteVimeoVideo(videoId: string) {
      try {
        await useActivityEditorStore().deleteVideo(videoId);
      } catch {
        useFeedback().error(`Une erreur est survenue`, "small");
      }
    },

    async importScormAPI(file: File, activityId: string) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ScormApi);
      try {
        const data = await apiInstance
          .importScormPackage({
            itemId: builderTrainingOwner().itemId,
            activityId,
            file,
          })
          .catch((error) => {
            feedback.error(`Une erreur est survenue`, "small");
            throw error;
          });

        this.currentActivity = data;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },
  },
});
